import * as React from 'react'
import Box from '@mui/material/Box'
import { TreeView } from '@mui/x-tree-view/TreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import MaterialSymbol from '../../../components/MaterialSymbol'
import { IconButton, Theme, Typography, styled, useMediaQuery } from '@mui/material'
import { colors } from '../../../themes/colors'
import TestIds from '../../../testIds'

interface RenderTree {
  id: string
  name: string
  children?: RenderTree[]
}

interface StyledTreeItemProps {
  last?: boolean
}

const StyledTreeItem = styled(TreeItem)<StyledTreeItemProps>`
  .Mui-selected {
    background: none !important;
  }
  .MuiTreeItem-content {
    padding-left: 0px;
    p {
      padding-left: 0px;
      margin-top: 6px;
      margin-bottom: 6px;
    }
    :focus,
    :hover {
      background: ${colors.primary100} !important;

      ::before {
        content: ' ';
        position: absolute;
        left: -999px;
        right: 0;
        top: 0;
        bottom: 0;
        z-index: 0; // Add this line
        background: ${colors.primary100} !important;
      }
    }
    .Mui-selected,
    .Mui-focused {
      background: none !important;
    }
    position: relative;

    ::after {
      content: '';
      position: absolute;
      left: -999px;
      right: 0;
      bottom: 0px;
      height: 2px;
      z-index: 9999; // Add this line
      border-bottom: 1px solid ${colors.primary100} !important;
    }
  }

  * .MuiTreeItem-iconContainer {
    /* padding: 0px 8px; */
    width: auto;
    height: 20px;
    z-index: 99;
  }
`
const ResponsiveTreeItem = styled(StyledTreeItem)<{ hasChildren: boolean; isMd: boolean }>(
  ({ hasChildren, isMd }) => ({
    '& .MuiTreeItem-iconContainer': {
      display: hasChildren || !isMd ? 'block' : 'none',
      paddingLeft: '0px',
      // paddingLeft: isMd ? 0 : undefined,
    },

    '& .MuiTreeItem-content': {
      paddingLeft: '0px',
      ...(hasChildren ? {} : { paddingLeft: '0px !important' }),
    },

    '& .MuiTreeItem-label': {
      paddingLeft: '0px',
      ...(hasChildren ? {} : { paddingLeft: '0px !important' }),
    },

    p: {
      paddingLeft: '0px',
      ...(hasChildren || !isMd ? {} : { paddingLeft: '0px !important' }),
    },
  }),
)

const ResponsiveTreeView = styled(TreeView)(({ theme }) => ({
  '& .MuiTreeItem-group': {
    marginLeft: '26px',
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },
}))

const DownloadButton = styled(IconButton)`
  position: absolute;
  right: -8px;
  top: 4px;
  bottom: 4px;
  color: ${colors.primary500};
  margin-right: 4px;
  border-radius: 4px;
`

interface RichObjectTreeViewProps {
  paths: string[]
  onClick: (path: string) => void
}

const convertPathToTree = (dataFromApi: string[]) => {
  const root = {
    id: 'declarations',
    name: 'Declarations',
    children: [],
  } as RenderTree

  dataFromApi.forEach((path) => {
    const pathParts = path.split('/')
    let currentNode = root
    pathParts.forEach((part, index) => {
      const foundNode = currentNode.children?.find((node) => node.name === part)
      const currentPath = pathParts.slice(0, index + 1).join('/') // Construct the directory path

      if (foundNode) {
        currentNode = foundNode
      } else {
        const newNode = {
          id: currentPath,
          name: part,
          children: [],
        }
        currentNode.children?.push(newNode as RenderTree)
        currentNode = newNode
      }
    })
  })

  return root
}

export default function RichObjectTreeView({ paths, onClick }: Readonly<RichObjectTreeViewProps>) {
  const data = convertPathToTree(paths)

  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const download = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, nodesId: string) => {
    event.stopPropagation()
    onClick(nodesId)
  }

  const renderLabel = (hasChildren: boolean, nodes: RenderTree) => {
    return (
      <Typography
        variant='body2'
        display={'inline-flex'}
        alignItems='center'
        justifyContent={'center'}
        padding='4px'
        gap='4px'
      >
        {hasChildren ? (
          <MaterialSymbol sx={{ color: colors.primary500, marginRight: '4px' }}>
            folder
          </MaterialSymbol>
        ) : null}
        <span style={{ paddingRight: '24px' }}>{nodes.name}</span>
        {!hasChildren && (
          <DownloadButton data-testid={TestIds.components.treeView.downloadFileButton}>
            <MaterialSymbol>download</MaterialSymbol>
          </DownloadButton>
        )}
      </Typography>
    )
  }

  const renderTree = (nodes: RenderTree) => {
    const isArray = Array.isArray(nodes.children)
    const hasChildren = (isArray && nodes.children && nodes.children.length > 0) ?? false
    return (
      <ResponsiveTreeItem
        hasChildren={hasChildren}
        isMd={isMd}
        key={nodes.id}
        nodeId={nodes.id}
        data-testid={TestIds.components.treeView.nodeTestId}
        data-id={TestIds.components.treeView.nodeDataId(nodes.name)}
        label={renderLabel(hasChildren, nodes)}
        onClick={(e) => {
          if (!hasChildren) {
            download(e, nodes.id)
          }
        }}
      >
        {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
      </ResponsiveTreeItem>
    )
  }

  return (
    <Box sx={{ minHeight: 500, flexGrow: 1, position: 'relative' }}>
      <ResponsiveTreeView
        data-testid={TestIds.components.treeView.treeView}
        aria-label='rich object'
        defaultCollapseIcon={
          <MaterialSymbol sx={{ color: colors.primary500 }}>expand_more</MaterialSymbol>
        }
        defaultExpanded={['root']}
        defaultExpandIcon={
          <MaterialSymbol sx={{ transform: 'rotate(-90deg)', color: colors.primary500 }}>
            expand_more
          </MaterialSymbol>
        }
      >
        {renderTree(data)}
      </ResponsiveTreeView>
    </Box>
  )
}
